<template>
  <div class="gmt-body">
    <div class="gmt-header">
      <div>
        <p class="gmt-header__title">Group Members</p>
        <p class="gmt-header__sub">User members with permissions to manage the group</p>
      </div>
      <hs-button v-if="canManageInvites" variant="primary" icon="user-plus" @click="showInviteModal = true">
        Add Member
      </hs-button>
    </div>

    <div v-if="members.length > 0 || searchQuery || roleFilter" class="gmt-filters">
      <hs-input v-model="searchQuery" icon="search" placeholder="Search by name or email…" label="Search" />
      <hs-select v-model="roleFilter" label="Role" placeholder="All Roles" :options="roleOptions" />
      <div class="gmt-filters__clear">
        <hs-button variant="ghost" icon="x" @click="clearFilters">Clear</hs-button>
      </div>
    </div>

    <div v-if="loading" class="gmt-state">
      <span class="gmt-spinner" />Loading members…
    </div>

    <hs-alert v-else-if="error" tone="danger" :message="error">
      <template #default>
        {{ error }}
        <hs-button variant="ghost" size="sm" @click="loadMembers">Retry</hs-button>
      </template>
    </hs-alert>

    <hs-empty-state
      v-else-if="members.length === 0 && !searchQuery && !roleFilter"
      icon="users"
      title="No members yet"
      description="No user members have been added to this group."
    />

    <hs-empty-state
      v-else-if="members.length === 0"
      icon="search"
      title="No results"
      description="No members match your filters."
    />

    <div v-else class="gmt-table-area">
      <hs-table-shell title="Group members" description="Users, roles, permissions, and membership actions.">
        <hs-data-table
          :headers="memberHeaders"
          :data="members"
          :header-key-mapping="memberHeaderKeyMapping"
        >
          <template #cell-member="{ row }">
            <div class="gmt-member-cell">
              <div class="gmt-avatar">{{ getInitials(row.user_name) }}</div>
              <div class="gmt-member-cell__copy">
                <span class="gmt-member-cell__name">{{ row.user_name || "Unknown User" }}</span>
                <span class="gmt-member-cell__email">{{ row.user_email || `User #${row.user_id}` }}</span>
              </div>
            </div>
          </template>

          <template #cell-role="{ value }">
            <hs-badge :tone="getRoleTone(value)" size="sm">{{ value || "MEMBER" }}</hs-badge>
          </template>

          <template #cell-permissions="{ row }">
            <div v-if="getDisplayPermissions(row.permissions).length" class="gmt-permission-chips">
              <hs-badge
                v-for="perm in getDisplayPermissions(row.permissions)"
                :key="perm"
                size="sm"
                tone="primary"
              >
                {{ perm }}
              </hs-badge>
            </div>
            <span v-else class="gmt-table__muted">No custom permissions</span>
          </template>

          <template #cell-joined="{ row }">
            {{ formatDate(row.created_at) }}
          </template>

          <template #cell-actions="{ row }">
            <div v-if="canManageInvites" class="gmt-table-actions">
              <hs-button
                variant="secondary"
                icon="edit-2"
                size="sm"
                :disabled="row.role === 'OWNER'"
                @click="editMember(row)"
              >
                Edit
              </hs-button>
              <hs-button
                variant="danger"
                icon="trash-2"
                size="sm"
                :disabled="row.role === 'OWNER'"
                @click="removeMember(row)"
              >
                Remove
              </hs-button>
            </div>
            <span v-else class="gmt-table__muted">No actions</span>
          </template>
        </hs-data-table>
      </hs-table-shell>

      <hs-pagination
        v-if="pagination.total > 0"
        :current-page="pagination.page"
        :total-pages="pagination.pages"
        :total-items="pagination.total"
        :items-per-page="pagination.perPage"
        @previous-page="handlePreviousPage"
        @next-page="handleNextPage"
        @update:itemsPerPage="handlePerPageChange"
      />
    </div>

    <invite-group-member-modal
      v-if="showInviteModal"
      :group-id="group.id"
      @close="showInviteModal = false"
      @invited="handleMemberInvited"
    />

    <edit-group-member-modal
      v-if="memberToEdit"
      :group-id="group.id"
      :member="memberToEdit"
      @close="memberToEdit = null"
      @updated="handleMemberUpdated"
    />

    <remove-member-confirmation-modal
      v-if="memberToRemove"
      :group-id="group.id"
      :member="memberToRemove"
      @close="memberToRemove = null"
      @removed="handleMemberRemoved"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import http from "@/helper/http";
import InviteGroupMemberModal from "./InviteGroupMemberModal.vue";
import EditGroupMemberModal from "./EditGroupMemberModal.vue";
import RemoveMemberConfirmationModal from "@/components/modals/RemoveMemberConfirmationModal.vue";
import { HsButton, HsInput, HsSelect, HsBadge, HsAlert, HsEmptyState, HsPagination, HsDataTable, HsTableShell } from "@/components/ui";

export default {
  name: "GroupMembersTab",
  components: {
    InviteGroupMemberModal, EditGroupMemberModal, RemoveMemberConfirmationModal,
    HsButton, HsInput, HsSelect, HsBadge, HsAlert, HsEmptyState, HsPagination, HsDataTable, HsTableShell,
  },
  props: {
    group: { type: Object, required: true },
  },
  emits: ["refresh"],
  setup(props) {
    const store = useStore();
    const loading = ref(false);
    const error = ref("");
    const members = ref([]);
    const showInviteModal = ref(false);
    const memberToEdit = ref(null);
    const memberToRemove = ref(null);
    const searchQuery = ref("");
    const roleFilter = ref("");

    const pagination = ref({ page: 1, perPage: 10, total: 0, pages: 0, hasNext: false, hasPrev: false });
    const memberHeaders = ["Member", "Role", "Permissions", "Joined", "Actions"];
    const memberHeaderKeyMapping = {
      Member: "user_name",
      Role: "role",
      Permissions: "permissions",
      Joined: "created_at",
      Actions: "actions",
    };

    const roleOptions = computed(() => [
      { label: "Owner", value: "OWNER" },
      { label: "Admin", value: "ADMIN" },
      { label: "Member", value: "MEMBER" },
      { label: "Read Only", value: "READ_ONLY" },
    ]);

    const canManageInvites = computed(() =>
      store.getters["groups/canManageInvites"] || store.getters["groups/isGroupOwner"]
    );

    const loadMembers = async () => {
      loading.value = true;
      error.value = "";
      try {
        const params = { page: pagination.value.page, per_page: pagination.value.perPage };
        if (searchQuery.value) { params.search_query = searchQuery.value; params.search_by = "user_name,user_email"; }
        if (roleFilter.value) params.role = roleFilter.value;

        const response = await http.get(`/v2/groups/${props.group.id}/members`, { params });
        const data = response.data || {};
        members.value = data.items || [];
        pagination.value = {
          page: data.page || 1,
          perPage: data.per_page || 10,
          total: data.total || 0,
          pages: data.pages || 0,
          hasNext: data.has_next || false,
          hasPrev: data.has_prev || false,
        };
      } catch (err) {
        console.error("Failed to load members", err);
        error.value = err?.response?.data?.error?.message || "Unable to load members.";
      } finally {
        loading.value = false;
      }
    };

    const handlePreviousPage = () => { if (pagination.value.hasPrev) { pagination.value.page -= 1; loadMembers(); } };
    const handleNextPage = () => { if (pagination.value.hasNext) { pagination.value.page += 1; loadMembers(); } };
    const handlePerPageChange = (perPage) => { pagination.value.perPage = perPage; pagination.value.page = 1; loadMembers(); };
    const clearFilters = () => { searchQuery.value = ""; roleFilter.value = ""; pagination.value.page = 1; };

    const getInitials = (name) => {
      if (!name) return "?";
      return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    };

    const getRoleTone = (role) => {
      const tones = { OWNER: "warning", ADMIN: "primary", MEMBER: "success", READ_ONLY: "neutral" };
      return tones[role] || "neutral";
    };

    const getDisplayPermissions = (permissions) => {
      if (!permissions || permissions.length === 0) return [];
      const display = permissions.slice(0, 3).map((p) =>
        p.replace("CAN_", "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())
      );
      if (permissions.length > 3) display.push(`+${permissions.length - 3} more`);
      return display;
    };

    const formatDate = (dateString) => {
      if (!dateString) return "Unknown";
      return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const editMember = (member) => { memberToEdit.value = member; };
    const removeMember = (member) => { memberToRemove.value = member; };
    const handleMemberUpdated = () => { memberToEdit.value = null; loadMembers(); };
    const handleMemberRemoved = () => { memberToRemove.value = null; loadMembers(); };
    const handleMemberInvited = () => { showInviteModal.value = false; loadMembers(); };

    watch([searchQuery, roleFilter], () => { pagination.value.page = 1; loadMembers(); });
    onMounted(() => { loadMembers(); });

    return {
      loading, error, members, showInviteModal, memberToEdit, memberToRemove,
      searchQuery, roleFilter, pagination, roleOptions, canManageInvites,
      memberHeaders, memberHeaderKeyMapping,
      loadMembers, handlePreviousPage, handleNextPage, handlePerPageChange, clearFilters,
      getInitials, getRoleTone, getDisplayPermissions, formatDate,
      editMember, removeMember, handleMemberUpdated, handleMemberRemoved, handleMemberInvited,
    };
  },
};
</script>

<style scoped>
.gmt-body {
  display: grid;
  gap: 1.1rem;
}

.gmt-header {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
}

.gmt-header__title {
  color: var(--hs-text);
  font-size: 0.95rem;
  font-weight: 750;
  margin: 0;
}

.gmt-header__sub {
  color: var(--hs-muted);
  font-size: 0.8rem;
  margin: 0.15rem 0 0;
}

.gmt-filters {
  align-items: flex-end;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr 1fr auto;
}

.gmt-filters__clear {
  padding-bottom: 0.1rem;
}

@media (max-width: 520px) {
  .gmt-filters { grid-template-columns: 1fr; }
}

.gmt-state {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  font-size: 0.875rem;
  gap: 0.65rem;
  justify-content: center;
  padding: 2.5rem 0;
}

.gmt-spinner {
  animation: gmt-spin 0.8s linear infinite;
  border: 2px solid color-mix(in srgb, var(--hs-primary) 28%, transparent);
  border-radius: 50%;
  border-top-color: var(--hs-primary);
  display: inline-block;
  height: 1.1rem;
  width: 1.1rem;
}

@keyframes gmt-spin { to { transform: rotate(360deg); } }

.gmt-table-area {
  display: grid;
  gap: 0.85rem;
}

.gmt-member-cell {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  min-width: 0;
}

.gmt-avatar {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary) 14%, transparent);
  border-radius: 50%;
  color: var(--hs-primary);
  display: flex;
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 800;
  height: 2.2rem;
  justify-content: center;
  width: 2.2rem;
}

.gmt-member-cell__copy {
  display: grid;
  gap: 0.1rem;
  min-width: 0;
}

.gmt-member-cell__name {
  color: var(--hs-text);
  font-size: 0.9rem;
  font-weight: 850;
}

.gmt-member-cell__email,
.gmt-table__muted {
  color: var(--hs-muted);
  font-size: 0.78rem;
}

.gmt-member-cell__email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gmt-permission-chips,
.gmt-table-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
</style>
